import { FC, useEffect, useState } from "react";
import { Box, Skeleton, Typography, Card } from "@mui/material";
import "@xyflow/react/dist/style.css";
import { ReactFlowProvider } from "@xyflow/react";
import OrgChartContext from "src/Pages/Company/OrganizationStructure/context/OrgchartContext";
import AreaMembersDialog from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Components/AreaMembersDialog";
import { useForm, FormProvider } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useLocation, useParams } from "react-router-dom";
import __ from "src/utils/Translations";
import useFetchArea from "src/Pages/Company/OrganizationStructure/api/useFetchArea";
import useGetEmployee from "src/Pages/Company/OrganizationStructure/api/useGetEmployee";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import FilterBar from "./FilterBar";
import OrgChartTree from "./OrgchartTree";
import { IOrgchartViewQueryParams, OrgchartType } from "./FilterBar.models";
import useOrgchartStore from "./Utils/store";
import useModifiedData from "./Utils/initialDataModification";
import changeNodesVisibility from "./Utils/changeNodesVisibility";
import { FilterBarSchema } from "./FilterBar.scheme";

interface QueryData {
  companyId: string;
  structureId: string;
}

const StructureView: FC<QueryData> = ({ structureId, companyId }) => {
  const location = useLocation();
  const logStatus = useLogStatus();
  const { orgchartType, nodeId } = useParams() as {
    orgchartType: OrgchartType;
    nodeId: string | undefined;
  };
  const searchBarEmployee = useGetEmployee({
    enabled: !!nodeId && orgchartType === OrgchartType.Employees,
    employeeId: nodeId || "",
  });

  const searchBarArea = useFetchArea({
    companyId,
    structureId,
    areaId: nodeId || "",
    enabled: !!nodeId && orgchartType === OrgchartType.Areas,
  });

  if (searchBarEmployee.error) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "StructureView: getEmployee failed",
      statusDetails: {
        source: "API",
        operation: "getEmployee",
        status: (searchBarEmployee.error as AxiosError)?.response?.status,
        url: (searchBarEmployee.error as AxiosError)?.config?.url,
      },
    });
  }
  if (searchBarArea.error) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "StructureView: fetchArea failed",
      statusDetails: {
        source: "API",
        operation: "fetchArea",
        status: (searchBarArea.error as AxiosError)?.response?.status,
        url: (searchBarArea.error as AxiosError)?.config?.url,
      },
    });
  }

  const [queryParams, setQueryParams] = useState<OrgchartType>(orgchartType);
  const { isLoading } = useModifiedData({
    companyId,
    structureId,
    orgchartType: queryParams,
  });
  const { updateIsLoading, areaDataForDialog } = useOrgchartStore();
  const updateQueryParameters = (params: IOrgchartViewQueryParams) => {
    setQueryParams(params.orgChartType);
  };

  const FilterBarDefaultValues: IOrgchartViewQueryParams = {
    autocomplete: "",
    orgChartType: orgchartType,
  };

  const methods = useForm<IOrgchartViewQueryParams>({
    resolver: standardSchemaResolver(FilterBarSchema),
    defaultValues: FilterBarDefaultValues,
  });

  useEffect(() => {
    if (!isLoading) {
      updateIsLoading(false);
    }
    // eslint-disable-next-line
  }, [isLoading]);

  useEffect(() => {
    if (
      searchBarEmployee.data &&
      nodeId &&
      orgchartType === OrgchartType.Employees
    ) {
      methods.setValue("autocomplete", {
        id: nodeId,
        label: `${searchBarEmployee.data.firstName} ${searchBarEmployee.data.lastName}`,
        initials: `${searchBarEmployee.data.firstName[0]}${searchBarEmployee.data.lastName[0]}`,
      });
    }
    if (searchBarArea.data && nodeId && orgchartType === OrgchartType.Areas) {
      methods.setValue("autocomplete", {
        id: searchBarArea.data.areaId as string,
        label: searchBarArea.data.name as string,
        initials: "",
        name: searchBarArea.data.name as string,
      });
    }
    // eslint-disable-next-line
  }, [orgchartType, searchBarEmployee.data, searchBarArea.data]);

  useEffect(() => {
    useOrgchartStore.setState(useOrgchartStore.getInitialState());
  }, [location.pathname]);

  return (
    <OrgChartContext.Provider value={{ changeNodesVisibility }}>
      <FormProvider {...methods}>
        <ReactFlowProvider>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <FilterBar
              updateQueryParameters={updateQueryParameters}
              methods={methods}
              companyId={companyId}
            />
            {isLoading ? (
              <Card sx={{ margin: "16px 0 0 0", padding: "0" }}>
                <Box
                  sx={{
                    backgroundColor: "#F0F7FF",
                    padding: "8px 16px",
                    borderBottom: "1px solid #A1DEFA",
                  }}
                >
                  <Typography fontWeight="fontWeightMedium">
                    {__(`Struktura`)}
                  </Typography>
                </Box>
                <Skeleton variant="rectangular" height={600} />
              </Card>
            ) : (
              <>
                {areaDataForDialog.name !== "" && (
                  <AreaMembersDialog
                    structureId={structureId}
                    companyId={companyId}
                  />
                )}
                <OrgChartTree orgchartType={queryParams} />
              </>
            )}
          </Box>
        </ReactFlowProvider>
      </FormProvider>
    </OrgChartContext.Provider>
  );
};

export default StructureView;
